import { z } from 'zod';
import * as zfd from 'zod-form-data';

import type {
    ActionFunctionArgs,
    LoaderFunctionArgs,
} from '~/types/route-args';
import { getDigitalPublicationOptions } from '@modules-rocco/subscription/api/get-digital-publication-options.server';
import { placeholderDigitalPublications } from '@modules-rocco/subscription/config/placeholder-publications';
import { getMemberSubscribeLocation } from '@modules-rocco/subscription/api/get-member-subscribe-location.server';
import {
    subscribeToDigitalPublications,
    type SubscribeToPublicationsResult,
} from '@modules-rocco/subscription/api/subscribe-to-digital-publications.server';
import { isSubscriberType } from '@modules-rocco/subscription/subscriber-type';
import type { SubscriberTypeEnum } from 'generated/graphql';

/** Publication catalogue for the subscribe modal, and the subscribe itself. */
export const SUBSCRIPTION_PUBLICATIONS_API_ROUTE =
    '/remix-api/subscription/publications';

export const loader = async ({ context }: LoaderFunctionArgs) => {
    const [catalogue, memberLocation] = await Promise.all([
        getDigitalPublicationOptions(context),
        // Null for a visitor, or a member who has never set one — the field then
        // behaves as it did before.
        getMemberSubscribeLocation(context),
    ]);

    // Outside production an empty catalogue falls back to placeholders, for the
    // same reason the articles publications row does: no environment but
    // production has publications configured, so the gate would otherwise open
    // on an empty pane and nothing about it could be reviewed. Production shows
    // only what the CMS holds.
    const options =
        catalogue.length === 0
            ? placeholderDigitalPublications(context.env.stage)
            : catalogue;

    return { options, memberLocation };
};

const subscribeSchema = zfd.formData({
    email: zfd.text(
        z
            .string()
            .min(1, { message: 'Email address is required' })
            .email('This is not a valid email address'),
    ),
    // Name, "I am a" and region are all required by HubSpot's form, but the
    // server checks them: it owns the messages, and the option lists they are
    // checked against.
    name: zfd.text(z.string().optional()),
    subscriberType: zfd.text(z.string().optional()),
    region: zfd.text(z.string().optional()),
    pageUri: zfd.text(z.string().optional()),
    pageName: zfd.text(z.string().optional()),
});

/** The cookie HubSpot's tracking script identifies the visitor by. */
const HUBSPOT_TRACKING_COOKIE = 'hubspotutk';

const getHubspotTrackingCookie = (request: Request): string | undefined =>
    request.headers
        .get('cookie')
        ?.split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${HUBSPOT_TRACKING_COOKIE}=`))
        ?.slice(HUBSPOT_TRACKING_COOKIE.length + 1);

export const action = async ({
    request,
    context,
}: ActionFunctionArgs): Promise<SubscribeToPublicationsResult> => {
    const parsed = subscribeSchema.safeParse(await request.formData());

    if (!parsed.success) {
        const issue = parsed.error.issues[0];
        return {
            success: false,
            message: issue?.message ?? 'Please check the form and try again.',
            // Only the email is checked here, so that is the field to mark.
            field: 'Email',
        };
    }

    const { email, name, subscriberType, region, pageUri, pageName } =
        parsed.data;

    return subscribeToDigitalPublications(
        {
            Input: {
                Email: email,
                Name: name || undefined,
                // A value the schema does not hold would fail GraphQL validation
                // and take the whole subscribe with it, so an unrecognised one is
                // dropped, and the server asks the reader to choose again.
                SubscriberType: isSubscriberType(subscriberType ?? null)
                    ? (subscriberType as SubscriberTypeEnum)
                    : undefined,
                Region: region || undefined,
            },
            // Ties the submission to the visitor HubSpot has been tracking, so it
            // joins their page history instead of starting a new contact trail.
            HubspotContext: {
                hutk: getHubspotTrackingCookie(request),
                pageUri: pageUri ?? '',
                pageName: pageName ?? '',
            },
        },
        context,
    );
};
